import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import {
  Button,
  DatePicker,
  message,
  Modal,
  Form as AntForm,
  Tag,
  Checkbox,
  Divider,
  Input,
  Select,
  InputNumber,
  Upload,
} from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import { MainTable, Form } from "components";
import ImagePreviewer from "./ImagePreviewer"; 
import { instance } from "utils/axios";
import fields from "./fields";
import { AuthContext } from "contexts";
import {
  CheckCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

function WoundMonitoring() {
  // dayjs plugins
  dayjs.extend(isBetween);
  dayjs.extend(advancedFormat);
  dayjs.extend(localizedFormat);
  dayjs.extend(weekday);
  dayjs.extend(localeData);

  // table & filters
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(3, "day").startOf("day")
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState("Бүгд");
  const [filterStatus, setFilterStatus] = useState("Идэвхтэй");

  // patient modal
  const [open, setOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form] = AntForm.useForm();
  const [editData, setEditData] = useState(null);

  // monitor modal
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [monitoringId, setMonitorId] = useState(null);
  const [monitorForm] = AntForm.useForm();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPatient, setDetailPatient] = useState(null);
  const [detailDate, setDetailDate] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [woundUser, setWoundUser] = useState(null);
  const [monitoring, setMonitoring] = useState(null);

  // refs/data
  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [symptomMap, setSymptomMap] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [symps, setSymps] = useState([]);
  const sympsLoadedRef = useRef(false);
  const [modalApi, contextHolder] = Modal.useModal();

  const authContext = useContext(AuthContext);
  const userId = authContext.state.userInfo?.profile?.id;

  // ===== PHOTO STATE (single) =====
  const [photoFile, setPhotoFile] = useState(null); // antd Upload file object
  const photoTempKeyRef = useRef(null); // key returned by /files/upload
  const initialPhotoUrlRef = useRef(null); // existing url when opening modal
  const didSaveRef = useRef(false); // set true only after submit

  // map: material id -> name (for details)
  const materialNameById = useMemo(() => {
    const m = new Map();
    (materials || []).forEach((it) => {
      const id = Number(it?.value ?? it?.id);
      const name = it?.label ?? it?.name ?? `#${id}`;
      if (!Number.isNaN(id)) m.set(id, name);
    });
    return m;
  }, [materials]);

  // helpers for photo
  const firstPhotoToFile = (url) =>
    url
      ? { uid: "existing-1", name: "photo", status: "done", url, thumbUrl: url }
      : null;
  const fileToPhotoUrl = (f) => {
    if (!f) return null;
    if (f.url) return f.url; // antd-ын url
    const r = f.response;
    if (!r) return null;
    if (typeof r === "string") return r; // серверээс string ирвэл шууд URL гэж үзнэ
    if (r.url) return r.url; // { url: "/uploads/..." } кейс
    return null;
  };
  
const deleteTempPhoto = async () => {
  const key = photoTempKeyRef.current;
  const url = fileToPhotoUrl(photoFile);
  if (!key && !url) return;
  try {
    await instance.post("/wound/files/delete", {
      key: key ?? undefined,
      url: key ? undefined : url,
    });
  } catch (_) {
    // swallow
  } finally {
    photoTempKeyRef.current = null;
  }
};

  // bootstrap data
  useEffect(() => {
    setLoading(true);
    instance
      .get("/systemusers", { params: { status: "Идэвхитэй", hospital: 2 } })
      .then((res) => {
        const normalize = (s) =>
          (s ?? "").toString().trim().toLocaleLowerCase("mn");
        const wanted = new Set(["сувилагч", "хэвтэн"]);
        const filtered = (Array.isArray(res?.data) ? res.data : []).filter(
          (item) => {
            const roles = Array.isArray(item?.role) ? item.role : [item?.role];
            return roles.some((r) => wanted.has(normalize(r)));
          }
        );
        const options = filtered.map((u) => {
          const ln = (u?.lastname ?? u?.lastName ?? "").toString().trim();
          const fn = (u?.firstname ?? u?.firstName ?? "").toString().trim();
          const label =
            `${ln ? `${ln[0].toUpperCase()}. ` : ""}${fn || ln}`.trim() ||
            `#${u?.id}`;
          return { value: u?.id, label, text: label };
        });
        setDoctors(options);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    setLoading(true);
    instance({ method: "get", url: `/surgery/types` })
      .then((res) => {
        setSurgeryTypes(
          res.data.map((item) => ({
            ...item,
            value: item.id,
            label: item.name,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    setLoading(true);
    instance({ method: "get", url: `/wound/materials` })
      .then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];
        setMaterials(
          arr
            .filter((m) => m?.active !== false)
            .map((m) => ({ value: m.id, label: m.name }))
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // date helpers
  const isToday = (d) => d.isSame(dayjs(), "day");
  const isYesterday = (d) => d.isSame(dayjs().subtract(1, "day"), "day");
  const isFuture = (d) => d.isAfter(dayjs(), "day");

  const renderStatusTag = (val, d, row) => {
    const key = dayjs(d).format("YYYY-MM-DD");
    const existing = row?.monitoringsByDate?.[key];

    if (isFuture(d)) return <Tag color="gold">-</Tag>;
    if (
      val == null ||
      val === false ||
      val === 0 ||
      (val?.trim && String(val).trim() === "")
    ) {
      return <Tag color="blue">*</Tag>;
    }
    return (
      <Tag color={val === "Цэвэр" ? "green" : "red"}>
        {String(val)}
        {existing?.woundTestTaken || existing?.catheterTestTaken
          ? "|Бактерлоги"
          : ""}
        {existing?.isCatheterRemoved ? "|Гуурс" : ""}
      </Tag>
    );
  };

  const pickDetails = (m) => {
    if (!m) return [];
    if (Array.isArray(m.details)) return m.details;
    if (Array.isArray(m.wound_monitoring_details))
      return m.wound_monitoring_details;
    return [];
  };

  const buildInitialValuesFromExisting = (existing, dateObj) => {
    setMonitorId(existing.id);
    const init = {
      sessionDate: (dateObj ?? dayjs()).startOf("day"),
      woundUserId:
        existing?.woundUserId || existing?.systemuser?.id || undefined,
      woundTestTaken: !!existing?.woundTestTaken,
      woundPathogen: existing?.woundPathogen ?? "",
      catheterTestTaken: !!existing?.catheterTestTaken,
      catheterPathogen: existing?.catheterPathogen ?? "",
      isCatheterRemoved: !!existing?.isCatheterRemoved,
      photoUrl: existing?.photoUrl ?? "",
    };

    const details = pickDetails(existing);
    for (const it of details) {
      const key = `sym_${it.symptomId}`;
      if (it.boolValue !== null && it.boolValue !== undefined)
        init[key] = !!it.boolValue;
      else if (it.numValue !== null && it.numValue !== undefined)
        init[key] = Number(it.numValue);
      else if (it.note) init[key] = String(it.note);
    }

    const mus =
      existing?.wound_material_usages ?? existing?.material_usages ?? [];
    if (Array.isArray(mus) && mus.length) {
      init.usages = mus.map((u) => ({
        materialId: u.materialId,
        qty: Number(u.qty ?? 1),
        note: u.note || "",
      }));
    }
    return init;
  };

  const daysInRange = useMemo(() => {
    const arr = [];
    if (!startDate || !endDate) return arr;
    if (startDate.isAfter(endDate)) return arr;
    let cur = startDate.startOf("day");
    const last = endDate.startOf("day");
    while (cur.isSame(last) || cur.isBefore(last)) {
      arr.push(cur);
      cur = cur.add(1, "day");
    }
    return arr;
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, filterType, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await instance.get("/wound/patients", {
        params: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          q: searchText || undefined,
          type: filterType || undefined,
          status: filterStatus || undefined,
        },
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const normalized = normalizeRows(rows);
      setData(normalized);
      generateColumns();
    } catch (err) {
      console.error(err);
      message.error("Өгөгдөл татахад алдаа гарлаа!");
    } finally {
      setLoading(false);
    }
  };

  const normalizeRows = (rows) =>
    rows.map((r, i) => {
      const copy = { ...r };
      copy.index = i + 1;
      copy.patientName = copy.patientName ?? copy.name ?? "-";
      copy.patientType = copy.patientType ?? copy.type ?? "-";

      const daysByDate = {};
      const monitoringsByDate = {};

      if (Array.isArray(r.wound_monitorings)) {
        r.wound_monitorings.forEach((m) => {
          if (!m?.sessionDate) return;
          const key = dayjs(m.sessionDate).format("YYYY-MM-DD");
          const materials =
            m?.wound_material_usages ?? m?.material_usages ?? [];
          const fullMonitoring = {
            ...m,
            wound_material_usages: materials,
            material_usages: materials,
          };
          const hasData = m.status;
          daysByDate[key] = hasData;
          monitoringsByDate[key] = fullMonitoring;
          copy[key] = daysByDate[key];
        });
      }

      for (const d of daysInRange) {
        const key = d.format("YYYY-MM-DD");
        if (copy[key] === undefined) copy[key] = null;
      }

      copy.daysByDate = daysByDate;
      copy.monitoringsByDate = monitoringsByDate;
      return copy;
    });

  const generateColumns = () => {
    const baseCols = [
      {
        title: "№",
        dataIndex: "index",
        key: "index",
        width: 50,
        fixed: "left",
      },
      {
        title: "Үйлчлүүлэгч",
        dataIndex: ["customer", "lastName"],
        key: "lastName",
        width: 120,
        fixed: "left",
        render: (text, row) => (
          <span>
            {text} {row.customer?.firstName}
          </span>
        ),
      },
      {
        title: "Регистер",
        dataIndex: ["customer", "regno"],
        key: "regno",
        width: 120,
      },
      {
        title: "Мэс заслын төрөл",
        dataIndex: "surgeryTypeId",
        key: "surgeryTypeId",
        width: 120,
        render: (text, row) => <span>{row.surgery_type?.name}</span>,
      },
      {
        title: "Мэс заслын огноо",
        dataIndex: "surgeryDate",
        key: "surgeryDate",
        width: 120,
      },
      { title: "Онош", dataIndex: "diagnosis", key: "diagnosis", width: 120 },
      {
        title: "Төрөл",
        dataIndex: "patientType",
        width: 160,
        key: "patientType",
        ellipsis: true,
        render: (text, row) => (
          <Select
            className="mb-0"
            value={row.patientType}
            disabled={row.status === "Дууссан"}
            options={[
              { value: "Хэвтэн", label: "Хэвтэн" },
              { value: "Амбулатори", label: "Амбулатори" },
            ]}
            onChange={(val) => handleSetType(val, row)}
          />
        ),
      },
      {
        title: "Гуурсны тоо",
        dataIndex: "catheterCount",
        key: "catheterCount",
        width: 85,
      },
      {
        title: "Гуурс авсан",
        dataIndex: "isCatheterRemoved",
        key: "isCatheterRemoved",
        width: 80,
        render: (text, row) =>
          row.catheterCount > 0 ? (
            <Tag color={text == 1 ? "green" : "red"}>
              {text == 1 ? "Тийм" : "Үгүй"}
            </Tag>
          ) : (
            <span />
          ),
      },
      {
        title: "Төлөв",
        dataIndex: "status",
        key: "status",
        width: 80,
        render: (text) => (
          <Tag color={text === "Идэвхтэй" ? "green" : "red"}>{text}</Tag>
        ),
      },
      {
        title: "Action",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status, row) => (
          <div className="flex items-center gap-5">
            <Button
              disabled={status === "Дууссан"}
              size="small"
              onClick={() => handleStatus(row)}
            >
              <div className="flex items-center gap-2">
                <CheckCircleOutlined /> Дуусгах
              </div>
            </Button>
          </div>
        ),
      },
    ];

    const dayCols = daysInRange.map((d) => {
      const key = d.format("YYYY-MM-DD");
      const label = d.format("MM-DD");
      return {
        title: label,
        dataIndex: key,
        key,
        onHeaderCell: () => ({
          style: { whiteSpace: "nowrap", textAlign: "center" },
        }),
        render: (val, row) => {
          if (!val && (isToday(d) || isYesterday(d))) {
            return (
              <Button
                size="small"
                onClick={() => handleOpenMonitor(row, d)}
                disabled={row.status === "Дууссан"}
                type={val ? "default" : "dashed"}
                block
              >
                {val ? "Засах" : "Боолт"}
              </Button>
            );
          }
          const handleClick = () => handleCellClick(row, d, val);
          return (
            <div
              className="flex justify-center"
              style={{ cursor: isFuture(d) ? "not-allowed" : "pointer" }}
              onClick={isFuture(d) ? undefined : handleClick}
              title={isFuture(d) ? "Ирээдүйн огноо" : "Дэлгэрэнгүй харах"}
            >
              {renderStatusTag(val, d, row)}
            </div>
          );
        },
      };
    });

    setColumns([...baseCols, ...dayCols]);
  };

  // stable order of symptoms
  const sympOrder = useMemo(() => {
    const m = new Map();
    (symps || []).forEach((s, i) => m.set(String(s.id), i));
    return m;
  }, [symps]);

  const handleCellClick = async (row, dateObj) => {
    if (isFuture(dateObj)) return;
    const key = dayjs(dateObj).format("YYYY-MM-DD");
    const existing = row?.monitoringsByDate?.[key];
    const dets = pickDetails(existing);

    try {
      setLoading(true);
      let mapRef = symptomMap;
      if (!Object.keys(mapRef).length) {
        const resp = await instance.get("/wound/symptoms");
        const m = {};
        (resp?.data || [])
          .filter((s) => s.isActive !== false)
          .forEach(
            (s) => (m[s.id] = s.labelMn || s.groupKey || `Symptom #${s.id}`)
          );
        setSymptomMap(m);
        mapRef = m;
      }

      const sorted = [...dets].sort((a, b) => {
        const ia = sympOrder.get(String(a.symptomId));
        const ib = sympOrder.get(String(b.symptomId));
        if (ia != null && ib != null) return ia - ib;
        if (ia != null) return -1;
        if (ib != null) return 1;
        return Number(a.symptomId) - Number(b.symptomId);
      });

      const items = sorted.map((it) => ({
        ...it,
        label: mapRef[it.symptomId] || `Symptom #${it.symptomId}`,
      }));

      setDetailPatient(row);
      setWoundUser(existing?.systemuser);
      setMonitoring(existing);
      setDetailDate(dateObj);
      setDetailItems(items);
      setDetailOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const stableSortSymps = (arr) =>
    [...arr].sort(
      (a, b) =>
        (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999) ||
        (Number(a.id) || 0) - (Number(b.id) || 0)
    );

  const handleOpenMonitor = async (row, dateObj) => {
    try {
      setLoading(true);
      setSelectedPatient(row);
      setSelectedDate(dateObj ?? null);
      didSaveRef.current = false;
      photoTempKeyRef.current = null;

      if (!sympsLoadedRef.current) {
        const resp = await instance.get("/wound/symptoms");
        const list = (resp?.data || []).filter((s) => s.isActive !== false);
        setSymps(stableSortSymps(list));
        sympsLoadedRef.current = true;
      }

      if (!doctors.length) {
        const res = await instance.get("/systemusers", {
          params: { status: "Идэвхитэй", hospital: 2 },
        });
        const normalize = (s) =>
          (s ?? "").toString().trim().toLocaleLowerCase("mn");
        const wanted = new Set(["сувилагч", "хэвтэн"]);
        const filtered = (Array.isArray(res?.data) ? res.data : []).filter(
          (item) => {
            const roles = Array.isArray(item?.role) ? item.role : [item?.role];
            return roles.some((r) => wanted.has(normalize(r)));
          }
        );
        const options = filtered.map((u) => {
          const ln = (u?.lastname ?? u?.lastName ?? "").toString().trim();
          const fn = (u?.firstname ?? u?.firstName ?? "").toString().trim();
          const label =
            `${ln ? `${ln[0].toUpperCase()}. ` : ""}${fn || ln}`.trim() ||
            `#${u?.id}`;
          return { value: u?.id, label, text: label };
        });
        setDoctors(options);
      }

      const key = dayjs(dateObj ?? dayjs()).format("YYYY-MM-DD");
      const existing = row?.monitoringsByDate?.[key] || null;
      const initValues = existing
        ? buildInitialValuesFromExisting(existing, dateObj)
        : {
            sessionDate: (dateObj ?? dayjs()).startOf("day"),
            woundUserId: userId,
            usages: [],
            photoUrl: "",
          };

      // sync photo preview & initial values
      initialPhotoUrlRef.current = initValues.photoUrl || null;
      setPhotoFile(firstPhotoToFile(initValues.photoUrl));

      setMonitorOpen(true);
      setTimeout(() => {
        monitorForm.resetFields();
        monitorForm.setFieldsValue(initValues);
      }, 0);
    } catch (e) {
      console.error(e);
      message.error("Хяналтын маягтыг бэлтгэхэд алдаа гарлаа!");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMonitor = async () => {
    const currentUrl = fileToPhotoUrl(photoFile);
    const initialUrl = initialPhotoUrlRef.current;
    const isNewUploaded = currentUrl && currentUrl !== initialUrl;

    if (!didSaveRef.current && isNewUploaded) {
      await deleteTempPhoto(); // ЭНД POST дуудна
    }

    setMonitorOpen(false);
    setSelectedPatient(null);
    setMonitorId(null);
    setSelectedDate(null);
    setPhotoFile(null);
    initialPhotoUrlRef.current = null;
    monitorForm.resetFields();
  };

  // Upload props (single image)
  const uploadProps = {
    listType: "picture-card",
    fileList: photoFile ? [photoFile] : [],
    maxCount: 1,
    accept: "image/*",
    onChange: async ({ fileList }) => {
      const prevUrl = fileToPhotoUrl(photoFile);
      const prevKey = photoTempKeyRef.current;

      const f = fileList[fileList.length - 1] || null;
      setPhotoFile(f || null);

      const newUrl = fileToPhotoUrl(f);
      const initialUrl = initialPhotoUrlRef.current;
      const prevWasTemp = prevUrl && prevUrl !== initialUrl && !!prevKey;
      if (prevWasTemp && newUrl !== prevUrl) {
        try {
          await instance.post("/wound/files/delete", { key: prevKey });
        } catch {}
        photoTempKeyRef.current = null;
      }
    },
    onRemove: async () => {
      const currentUrl = fileToPhotoUrl(photoFile);
      const initialUrl = initialPhotoUrlRef.current;
      const isTemp = currentUrl && currentUrl !== initialUrl;
      if (isTemp) {
        await instance.post("/wound/files/delete", { url: currentUrl });
      }
      setPhotoFile(null);
    },

    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const formData = new FormData();
        formData.append("image", file);
        const resp = await instance.post("/wound/files/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // resp.data нь ихэвчлэн string ("/uploads/..") байдаг гэж үзээд normalize хийе
        const data = resp?.data;
        const normalized =
          typeof data === "string"
            ? { url: data } // string бол url талбар болгон өгнө
            : {
                url: data?.url || "",
                key: data?.key || data?.fileKey || data?.id,
              };

        photoTempKeyRef.current = normalized.key || null;

        // antd-д үр дүнг өгнө (url нь File.item дээр бичигдэнэ)
        onSuccess(normalized);
      } catch (e) {
        onError(e);
      }
    },
  };

  // monitor form fields (with photo & materials)
  const monitorFields = useMemo(() => {
    const mapType = (t) => {
      const l = String(t || "boolean").toLowerCase();
      if (["boolean", "bool", "checkbox"].includes(l)) return "checkbox";
      if (["number", "decimal", "int"].includes(l)) return "number";
      if (["text", "textarea", "string"].includes(l)) return "textarea";
      return "checkbox";
    };

    return [
      {
        label: "Хяналтын огноо",
        name: "sessionDate",
        type: "date",
        rules: [{ required: true, message: "Огноо сонгоно уу!" }],
        className: "col-span-6 mb-2",
        disabled: true,
        inputProps: { className: "w-full" },
      },
      {
        label: "Боолт хийсэн",
        name: "woundUserId",
        type: "select",
        rules: [{ required: true, message: "Боолт хийсэн хүн  сонгоно уу!" }],
        className: "col-span-12 md:col-span-6 mb-0",
        inputProps: { className: "w-full", options: doctors },
      },

      // PHOTO (one button, no description)
      {
        type: "component",
        component: (
          <>
            <Divider className="col-span-12">Зураг</Divider>
            <div className="col-span-12">
              <Upload {...uploadProps}>{photoFile ? null : "+ Зураг"}</Upload>
              <div className="text-xs text-gray-500 mt-1">
                Зөвхөн 1 зураг. Хадгалахгүй хаавал зургийг устгана.
              </div>
            </div>
            {/* hidden field to keep photo url in form */}
            <AntForm.Item name="photoUrl" hidden>
              <Input />
            </AntForm.Item>
          </>
        ),
      },

      ...(symps || []).map((s) => ({
        label: s.labelMn || s.groupKey || `Symptom #${s.id}`,
        name: `sym_${s.id}`,
        type: mapType(s.valueType),
        className: "col-span-6 md:col-span-6 mb-1",
        inputProps: {
          className: "w-full",
          ...(mapType(s.valueType) === "number" && { min: 0 }),
        },
      })),

      {
        label: "Гуурс авсан",
        name: "isCatheterRemoved",
        type: "checkbox",
        className: "col-span-12 md:col-span-6 mb-0",
      },

      {
        type: "component",
        component: (
          <Divider className="col-span-12">Бактерлоги мэдээлэл</Divider>
        ),
      },

      {
        label: "Шархнаас нян судлалын шинжилгээ авсан эсэх",
        name: "woundTestTaken",
        type: "checkbox",
        className: "col-span-12 md:col-span-6 mb-0",
      },
      {
        label: "Илэрсэн үүсгэгч",
        name: "woundPathogen",
        type: "input",
        className: "col-span-12 md:col-span-6 mb-0",
      },

      {
        label: "Хэвлийн гуурснаас нян судлалын шинжилгээ авсан эсэх",
        name: "catheterTestTaken",
        type: "checkbox",
        className: "col-span-12 md:col-span-6 mb-0",
      },
      {
        label: "Илэрсэн үүсгэгч",
        name: "catheterPathogen",
        type: "input",
        className: "col-span-12 md:col-span-6 mb-0",
      },

      {
        type: "component",
        component: (
          <>
            <Divider className="col-span-12">Материалын хэрэглээ</Divider>
            <AntForm.List
              name="usages"
              initialValue={[{ materialId: undefined, qty: 1, note: "" }]}
            >
              {(fields, { add, remove }) => (
                <div className="col-span-12 space-y-2">
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      className="grid grid-cols-12 gap-2 items-center border rounded-lg p-2"
                    >
                      <div className="col-span-6 md:col-span-5">
                        <AntForm.Item
                          {...field}
                          name={[field.name, "materialId"]}
                          rules={[
                            { required: true, message: "Материал сонгоно уу" },
                          ]}
                          className="mb-0"
                        >
                          <Select
                            showSearch
                            options={materials}
                            placeholder="Материал..."
                            optionFilterProp="label"
                          />
                        </AntForm.Item>
                      </div>
                      <div className="col-span-3 md:col-span-2">
                        <AntForm.Item
                          {...field}
                          name={[field.name, "qty"]}
                          rules={[{ required: true, message: "Тоо хэмжээ" }]}
                          className="mb-0"
                        >
                          <InputNumber
                            min={0.001}
                            step={0.5}
                            precision={3}
                            className="w-full"
                            placeholder="Хэмжээ"
                          />
                        </AntForm.Item>
                      </div>
                      <div className="col-span-10 md:col-span-3">
                        <AntForm.Item
                          {...field}
                          name={[field.name, "note"]}
                          className="mb-0"
                        >
                          <Input placeholder="Тайлбар (сонголттой)" />
                        </AntForm.Item>
                      </div>
                      <div className="col-span-2 md:col-span-1 flex justify-end">
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => add({ qty: 1 })}
                  >
                    Материал нэмэх
                  </Button>
                </div>
              )}
            </AntForm.List>
          </>
        ),
      },
    ];
  }, [symps, doctors, materials, photoFile]);

  const renderDetailValue = (it) => {
    const hasBool = it.boolValue !== null && it.boolValue !== undefined;
    const hasNum = it.numValue !== null && it.numValue !== undefined;
    const hasNote = it.note && String(it.note).trim() !== "";
    if (hasBool) return <Checkbox checked={it.boolValue} disabled />;
    if (hasNum) return <Tag color="blue">{it.numValue}</Tag>;
    if (hasNote)
      return <span className="whitespace-pre-wrap break-words">{it.note}</span>;
    return <span className="text-gray-400">-</span>;
  };

  const handleSubmitMonitor = async (values) => {
    try {
      if (!selectedPatient?.id)
        return message.error("Өвчтөн сонгогдоогүй байна!");

      const computedDate = values.sessionDate
        ? dayjs(values.sessionDate)
        : selectedDate
        ? selectedDate
        : dayjs();
      const sessionDate = computedDate.format("YYYY-MM-DD");

      let status = "Цэвэр";
      const items = [];
      for (const f of monitorFields) {
        if (f.name === "sessionDate" || f.name === "woundUserId") continue;
        const m = String(f.name || "");
        if (!m.startsWith("sym_")) continue;
        const symptomId = Number(m.replace("sym_", ""));
        const v = values[f.name];

        if (f.type === "checkbox") {
          items.push({ symptomId, boolValue: !!v, numValue: null, note: null });
          if (v) status = "Өөрчлөлттэй";
        } else if (f.type === "number") {
          if (v !== undefined && v !== null && v !== "") {
            items.push({
              symptomId,
              boolValue: null,
              numValue: Number(v),
              note: null,
            });
          }
        } else {
          if (v && String(v).trim() !== "") {
            items.push({
              symptomId,
              boolValue: null,
              numValue: null,
              note: String(v),
            });
          }
        }
      }

      if (!items.length)
        return message.warning("Оруулах утга сонгоогдоогүй байна.");

      const usages = (values?.usages || [])
        .filter((u) => u && u.materialId && u.qty)
        .map((u) => ({
          materialId: Number(u.materialId),
          qty: Number(u.qty),
          note: u?.note ? String(u.note) : undefined,
        }));

      const uploadedUrl = fileToPhotoUrl(photoFile);

      await instance.post("/wound/monitoring/create", {
        woundPatientId: selectedPatient.id,
        sessionDate,
        woundUserId: values.woundUserId,
        monitoringId: monitoringId,
        status,
        wpType: selectedPatient.type,
        woundTestTaken: values.woundTestTaken,
        woundPathogen: values.woundPathogen,
        catheterPathogen: values.catheterPathogen,
        isCatheterRemoved: values.isCatheterRemoved,
        catheterTestTaken: values.catheterTestTaken,
        photoUrl: uploadedUrl || null,
        items,
        materialUsages: usages,
      });

      didSaveRef.current = true; // prevent deleting saved photo
      message.success("Хяналт амжилттай хадгаллаа");
      handleCloseMonitor();
      fetchData();
    } catch (e) {
      console.error(e);
      message.error("Хадгалах явцад алдаа гарлаа!");
    }
  };

  const handleOpenAdd = () => {
    setModalMode("create");
    setEditingId(null);
    setEditData({
      patient: {
        type: "Амбулатори",
        admissionDate: dayjs(),
        surgeryDate: dayjs(),
        diagnosis: "",
        note: "",
      },
    });
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setModalMode("edit");
    setEditingId(row?.id ?? null);
    setOpen(true);
    form.setFieldsValue({
      ...row,
      admissionDate: row?.admissionDate ? dayjs(row.admissionDate) : null,
    });
  };

  const handleSubmit = (values) => {
    const type = values?.type || "Хэвтэн";
    const admission = values?.admissionDate
      ? dayjs(values.admissionDate)
      : dayjs();
    const payload = {
      ...values,
      type,
      admissionDate: admission.format("YYYY-MM-DD"),
    };
    const method = modalMode === "edit" ? "put" : "post";
    const url =
      modalMode === "edit"
        ? `/wound/patient/update/${editingId}`
        : "/wound/patient/create";

    setLoading(true);
    instance({ method, url, data: payload })
      .then(() => {
        message.success("Амжилттай хадгалагдлаа");
        fetchData();
        handleCloseModal();
      })
      .catch(() => message.error("Хадгалах явцад алдаа гарлаа!"))
      .finally(() => setLoading(false));
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleSetType = async (value, row) => {
    const data = { id: row.id, type: value ?? null };
    await modalApi.confirm({
      title: "Анхаарна уу !",
      content: "Өмнө тохируулсан 'Төрөл' байна. Солихдоо итгэлтэй байна уу?",
      okText: "Тийм",
      cancelText: "Үгүй",
      onOk() {
        instance({ method: "put", url: `/wound/patient/type`, data })
          .then(() => fetchData())
          .catch((err) => {
            if (err?.response?.status === 403) {
              modalApi.warning({
                title: "Анхаарна уу !",
                content: "Хэрэглэгчийн эрх хүрэхгүй байна",
              });
            }
          });
      },
      onCancel() {},
    });
  };

  const handleStatus = (values) => {
    modalApi.confirm({
      title: "Анхаарна уу !",
      content: "Шарх боолт дууссан гэдэгт итгэлтэй байна уу",
      okText: "Тийм",
      cancelText: "Үгүй",
      onOk() {
        return new Promise((resolve, reject) => {
          instance({ method: "put", url: `/wound/patient/finish/${values.id}` })
            .then(() => resolve())
            .catch((err) => {
              if (err.response?.status === 403) {
                modalApi.warning({
                  title: "Анхаарна уу !",
                  content: "Хэрэглэгчийн эрх хүрэхгүй байна",
                });
              }
              reject();
            })
            .then(() => fetchData());
        }).catch(() => {});
      },
      onCancel() {},
    });
  };

  return (
    <div className="flex flex-col px-4 pb-4">
      {/* FILTERS */}
      <div className="grid grid-cols-24 gap-2">
        <div className="col-span-24 md:col-span-10">
          <div className="flex items-center gap-2">
            <DatePicker
              className="w-[140px]"
              value={startDate}
              onChange={(e) => setStartDate(e)}
            />
            <DatePicker
              className="w-[140px]"
              value={endDate}
              onChange={(e) => setEndDate(e)}
            />
            <Select
              className="mb-0 w-[140px]"
              placeholder="Бүгд"
              value={filterType}
              onChange={setFilterType}
              options={[
                { value: "Бүгд", label: "Бүгд" },
                { value: "Хэвтэн", label: "Хэвтэн" },
                { value: "Амбулатори", label: "Амбулатори" },
              ]}
            />
            <span>Төлөв:</span>
            <Select
              className="mb-0 w-[140px]"
              placeholder="Бүгд"
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: "Бүгд", label: "Бүгд" },
                { value: "Идэвхтэй", label: "Идэвхтэй" },
                { value: "Дууссан", label: "Дууссан" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* search */}
      <div className="mt-2 flex items-center gap-2 flex-wrap mb-2">
        <span>Нэр/РД хайх:</span>
        <Input
          className="mb-0 w-[320px]"
          placeholder="Овог нэр эсвэл Регистрийн дугаар"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={fetchData}
          allowClear
        />
        <Button
          type="primary"
          onClick={fetchData}
          loading={loading}
          icon={<SearchOutlined />}
        >
          Хайх
        </Button>
      </div>

      {/* main table */}
      <MainTable
        dataTable={data}
        columns={columns}
        loading={loading}
        setCurrentPage={setCurrentPage}
        handleAdd={handleOpenAdd}
        handleRefresh={fetchData}
        title="Боолт хяналт (сонгосон огнооны хооронд)"
        scroll={{ y: "calc(100vh - 320px)", x: "max-content" }}
      />

      {/* patient add/edit */}
      <Modal
        title={
          modalMode === "edit" ? "Боолт хяналт засах" : "Боолт хяналт нэмэх"
        }
        open={open}
        onCancel={handleCloseModal}
        onOk={form.submit}
        confirmLoading={loading}
        width={1000}
        destroyOnClose
        okText="Хадгалах"
      >
        <Form
          key={modalMode}
          form={form}
          fields={fields({ form, surgeryTypes })}
          layout="vertical"
          onFinish={handleSubmit}
          className="gap-4"
          editData={editData}
        />
      </Modal>

      {/* monitoring create/edit */}
      <Modal
        title="Боолтын өдөр тутмын хяналт"
        open={monitorOpen}
        onCancel={handleCloseMonitor}
        onOk={monitorForm.submit}
        confirmLoading={loading}
        width={800}
        destroyOnClose
        okText="Хадгалах"
      >
        <Form
          form={monitorForm}
          fields={monitorFields}
          layout="vertical"
          onFinish={handleSubmitMonitor}
          className="gap-4"
        />
      </Modal>

      {/* details */}
      <Modal
        title="Өдрийн дэлгэрэнгүй (унших)"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetailPatient(null);
          setDetailDate(null);
          setDetailItems([]);
        }}
        footer={[
          <Button
            key="edit"
            type="primary"
            disabled={detailPatient?.status === "Дууссан"}
            onClick={() => {
              setDetailOpen(false);
              if (detailPatient && detailDate)
                handleOpenMonitor(detailPatient, detailDate);
            }}
          >
            Засах / Нэмэх
          </Button>,
          <Button key="close" onClick={() => setDetailOpen(false)}>
            Хаах
          </Button>,
        ]}
        width={800}
        destroyOnClose
      >
        <div className="space-y-3">
          <div className="text-sm text-gray-500">
            Өвчтөн: <b>{detailPatient?.customer?.lastName}</b>&nbsp;
            <b>{detailPatient?.customer?.firstName}</b>
            {detailPatient?.customer?.regno
              ? ` (${detailPatient.customer.regno})`
              : ""}
          </div>
          <div className="text-sm text-gray-500">
            Огноо:{" "}
            <b>{detailDate ? dayjs(detailDate).format("YYYY-MM-DD") : "-"}</b>
          </div>
          <div className="text-sm text-gray-500">
            Боолт хийсэн:{" "}
            <b>
              {(woundUser?.lastname?.charAt(0) || "") + ". "}
              {woundUser?.firstname || ""}
            </b>
          </div>

          {detailItems?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {detailItems.map((it, idx) => {
                const isYes = it.boolValue === true;
                return (
                  <div
                    key={idx}
                    className={
                      "rounded-lg border p-3 flex items-start justify-between " +
                      (isYes ? "bg-red-50 border-red-200" : "")
                    }
                  >
                    <div className="font-medium pr-3">{it.label}</div>
                    <div className="ml-2 text-right">
                      {renderDetailValue(it)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-gray-500">Мэдээлэл алга байна.</div>
          )}

          <Divider className="col-span-12">Зураг</Divider>
          <div className="flex items-center gap-2">
            {monitoring?.photoUrl ? (
              <Button type="default" onClick={() => setPreviewOpen(true)}>
                Зураг харах
              </Button>
            ) : (
              <div className="text-sm text-gray-500">Зураг: мэдээлэл алга</div>
            )}
          </div>

          <ImagePreviewer
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            images={
              monitoring?.photoUrl
                ? [`${process.env.REACT_APP_CDN_URL}${monitoring?.photoUrl}`]
                : []
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="text-sm text-gray-500">
              Гуурс авсан{" "}
              <Checkbox checked={monitoring?.isCatheterRemoved} disabled />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="text-sm text-gray-500">
              Шархнаас нян судлалын шинжилгээ{" "}
              <Checkbox checked={monitoring?.woundTestTaken} disabled />
            </div>
            <div className="text-sm text-gray-500">
              Илэрсэн үүсгэгч : <b>{monitoring?.woundPathogen}</b>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="text-sm text-gray-500">
              Хэвлийн гуурснаас нян судлалын шинжилгэ{" "}
              <Checkbox checked={monitoring?.catheterTestTaken} disabled />
            </div>
            <div className="text-sm text-gray-500">
              Илэрсэн үүсгэгч : <b>{monitoring?.catheterPathogen}</b>
            </div>
          </div>

          <Divider className="col-span-12">Материалын хэрэглээ</Divider>
          {(() => {
            const mus =
              monitoring?.wound_material_usages ??
              monitoring?.material_usages ??
              [];
            if (!Array.isArray(mus) || mus.length === 0) {
              return (
                <div className="text-sm text-gray-500">
                  Материалын хэрэглээ: <b>мэдээлэл алга</b>
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {mus.map((u, idx) => {
                  const name =
                    materialNameById.get(Number(u.materialId)) ||
                    `#${u.materialId}`;
                  return (
                    <div
                      key={idx}
                      className="rounded-lg border p-3 flex items-center justify-between"
                    >
                      <div className="font-medium">{name}</div>
                      <div className="flex items-center gap-3">
                        <Tag>{Number(u.qty)}</Tag>
                        {u.note ? (
                          <span className="text-gray-500 text-sm">
                            {u.note}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </Modal>

      {contextHolder}
    </div>
  );
}

export default WoundMonitoring;
